import * as Excel from "exceljs";
import FileSaver from "file-saver";
import { models } from "types/api/viewModels";
import { getFileNameBasedOnCurrentDateTime } from "utils/file";
import { checkForAndReplaceNullEmptyWhitespace, convertToTitleCase } from "utils/string";
import { dateFormat } from "utils/date";
import { ReplacmentConfirmationOrderPending, TruckPendingText, UnavailableDataPlaceholderText } from "constants/text";

export function convertTruckDetailsToExcel(truck: models["TruckViewModel"]) {
	return {
		"Truck Number": truck.truckNumber ?? TruckPendingText,
		"Truck Name": checkForAndReplaceNullEmptyWhitespace(truck.truckName),
		"Account Number": checkForAndReplaceNullEmptyWhitespace(truck.accountNumber),
		"Account Name": checkForAndReplaceNullEmptyWhitespace(truck.accountName),
		"Product Line": checkForAndReplaceNullEmptyWhitespace(truck.productLine?.description),
		"Truck Status": checkForAndReplaceNullEmptyWhitespace(convertToTitleCase(truck.truckStatus)),
		"Order Date": truck.submittedOn ? dateFormat(String(truck.submittedOn)) : UnavailableDataPlaceholderText,
		"Requested Delivery Date": checkForAndReplaceNullEmptyWhitespace(
			dateFormat(String(truck.requestedDeliveryDate))
		),
		"Estimated Delivery Date": truck.stops?.[0]?.orders?.[0]?.eta
			? dateFormat(String(truck.stops[0].orders[0].eta))
			: ReplacmentConfirmationOrderPending,
		"Cabinet Count": truck.cabinetCount ?? UnavailableDataPlaceholderText,
		"Cubes Count": truck.cubeCount ?? UnavailableDataPlaceholderText,
		"Cubes Available": truck.cubeCapacity ?? UnavailableDataPlaceholderText
	};
}

export function convertTruckOrdersToExcel(stops: models["TruckStopViewModel"][] | null | undefined) {
	if (!stops || stops.length === 0) {
		return [];
	}

	return stops.flatMap((stop) =>
		stop.orders?.map((order) => ({
			"Account Number": checkForAndReplaceNullEmptyWhitespace(order.accountNumber),
			"Order Number": checkForAndReplaceNullEmptyWhitespace(order.orderNumber),
			"PO Number": checkForAndReplaceNullEmptyWhitespace(order.poNumber),
			"Job Name": checkForAndReplaceNullEmptyWhitespace(order.jobName),
			"Order Type": checkForAndReplaceNullEmptyWhitespace(convertToTitleCase(order.orderType)),
			"Designer Number": checkForAndReplaceNullEmptyWhitespace(order.designer?.designerNumber),
			"Order Status": checkForAndReplaceNullEmptyWhitespace(convertToTitleCase(order.status)),
			"Order Date": order.orderDate ? dateFormat(String(order.orderDate)) : UnavailableDataPlaceholderText,
			"Cabinet Count": order.counts?.cabinetCount ?? UnavailableDataPlaceholderText,
			"Accessory Count": order.counts?.accessoryCount ?? UnavailableDataPlaceholderText,
			"Cube Count": order.counts?.cubeCount ?? UnavailableDataPlaceholderText
		}))
	);
}

export async function generateTruckDetailsExcel(truck: models["TruckViewModel"]) {
	const filePrefix = `${truck.truckName}_${truck.truckNumber ?? "#" + TruckPendingText}`;
	const workbook = new Excel.Workbook();
	const truckWorksheet = workbook.addWorksheet("Truck");
	const truckOrdersWorksheet = workbook.addWorksheet("Orders on Truck");

	const truckInfo = convertTruckDetailsToExcel(truck);
	const truckOrdersInfo = convertTruckOrdersToExcel(truck.stops);

	truckWorksheet.columns = Object.keys(truckInfo).map((col) => {
		return { key: col, header: col };
	});
	truckWorksheet.addRow(truckInfo);

	if (truckOrdersInfo.length > 0) {
		truckOrdersWorksheet.columns = Object.keys(truckOrdersInfo[0] ?? {}).map((col) => {
			return { key: col, header: col };
		});
		truckOrdersWorksheet.addRows(truckOrdersInfo);
	}

	try {
		workbook.xlsx.writeBuffer().then((data) => {
			const blob = new Blob([data], {
				type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8"
			});
			FileSaver.saveAs(blob, `${getFileNameBasedOnCurrentDateTime(filePrefix)}.xlsx`);
		});
	} catch (e) {
		console.error("Error trying to generate truck details xlsx file", e);
	}
}
